'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import FrostLogo from '@/components/shared/FrostLogo';
import { CONTACT_EMAIL } from '@/lib/constants';
import type { CSSProperties } from 'react';

const NAV_LINKS = [
  { href: '/services', label: 'Services' },
  { href: '/industries', label: 'Industries' },
  { href: '/areas', label: 'Areas' },
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Journal' }
];

function isActive(pathname: string, href: string) {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

function BurgerIcon({ open }: { open: boolean }) {
  return (
    <span className={`burger ${open ? 'burger--open' : ''}`} aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

export default function Header() {
  const pathname = usePathname() ?? '/';
  const [open, setOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const firstLink = sheetRef.current?.querySelector<HTMLAnchorElement>(
      '[data-sheet-first]'
    );
    firstLink?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        openerRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const focusables = sheetRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;
      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  return (
    <>
      <nav className="fb-nav" aria-label="Main navigation">
        <Link href="/" className="fb-nav__brand" aria-label="Frostbark Digital home">
          <FrostLogo />
          <span>
            Frostbark<span className="mono">/ DIGITAL</span>
          </span>
        </Link>

        <div className="fb-nav__links">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={isActive(pathname, link.href) ? 'active' : ''}
              aria-current={isActive(pathname, link.href) ? 'page' : undefined}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <Link href="/contact" className="fb-btn fb-btn--primary fb-nav__cta">
          Start a project
          <span className="fb-btn__arrow">→</span>
        </Link>

        <button
          ref={openerRef}
          type="button"
          className="fb-nav__toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen((v) => !v)}
        >
          <BurgerIcon open={open} />
        </button>
      </nav>

      <div
        className={`fb-sheet-backdrop ${open ? 'open' : ''}`}
        onClick={close}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        id="mobile-nav"
        className={`fb-sheet ${open ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        <div className="fb-sheet__head">
          <span className="marker fb-sheet__title">Menu</span>
          <button
            type="button"
            className="fb-sheet__close"
            aria-label="Close menu"
            onClick={close}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <nav className="fb-sheet__links" aria-label="Mobile">
          {NAV_LINKS.map((link, i) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={close}
              data-sheet-first={i === 0 ? '' : undefined}
              className={isActive(pathname, link.href) ? 'active' : ''}
              aria-current={isActive(pathname, link.href) ? 'page' : undefined}
              style={{ '--i': i } as CSSProperties}
            >
              <span className="fb-sheet__link-num">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="fb-sheet__link-label">{link.label}</span>
              <span className="fb-sheet__link-arrow" aria-hidden="true">
                →
              </span>
            </Link>
          ))}
        </nav>

        <div className="fb-sheet__footer">
          <a href={`mailto:${CONTACT_EMAIL}`} className="fb-sheet__email">
            {CONTACT_EMAIL}
          </a>
          <div className="fb-sheet__meta">
            <span>West Palm Beach</span>
            <span>/</span>
            <span>Replies within one business day</span>
          </div>
        </div>
      </div>
    </>
  );
}
